import routes, {
  procurementRoute,
  procurementManagerRoutes,
  managerOrderApprovalRoutes,
} from "routes"
import { getRoleMenu } from "menus/registry"
import { isManagerRole } from "shared/procurement/eligibleRoles"
import { PROCUREMENT_MENU_ACCESS, DEPARTMENT_MANAGER_ACCESS } from "shared/procurement/menuAccess"
import { Role, getRoleCode } from "constant/Role"

// Single source of truth for "which route entries can this user's active
// role actually reach" — App.js uses it to build the sidenav/route tree, and
// any page that renders its own tab-switcher (e.g. ManagerWorkspaceHeader)
// uses it to hide tabs the user can't open, instead of hardcoding a role
// table that inevitably drifts from the real routing rules.

// Filters a route tree down to `access.visible` (kept as-is, and only
// recursed into if it has a `collapse`) plus `access.hidden` (kept but
// forced to `type: "hidden"` — App.js's getRoutes still registers a <Route>
// for these regardless of `type`, so a detail/form screen reached by
// clicking a table row stays reachable even though its parent list item
// isn't on the menu). A group whose children are all filtered away is
// dropped too, so the sidebar never shows an empty collapse. Anything in
// neither set is dropped entirely.
const filterTree = (items = [], access) =>
  items.reduce((acc, item) => {
    if (item.type === "divider") return acc.length ? [...acc, item] : acc
    if (access.hidden.has(item.key)) return [...acc, { ...item, type: "hidden" }]
    if (!access.visible.has(item.key)) return acc
    if (!item.collapse) return [...acc, item]
    const collapse = filterTree(item.collapse, access)
    return collapse.some((c) => c.type !== "hidden" && c.type !== "divider")
      ? [...acc, { ...item, collapse }]
      : acc
  }, [])

// Resolves the exact list of top-level menu entries (with nested `collapse`
// arrays intact) the given auth state's active role can see — mirrors
// App.js's `currentRoutesOnRole` exactly, so anything routable there is
// discoverable here too.
export const getAccessibleRoutes = (auth, { isDepartmentManager = false } = {}) => {
  const entry = getRoleMenu(auth?.role)

  let baseForRole = entry?.menu || routes
  if (entry?.transformForUser) {
    baseForRole = entry.transformForUser(baseForRole, {
      isManager: !!auth?.user?.isManager,
    })
  }

  const activeRoleCode = getRoleCode(auth?.role)
  // Department managers get the staff tree plus the department-scoped lists and
  // inboxes, whatever their role — unless the role already has a PM/Finance tree.
  const roleAccess = PROCUREMENT_MENU_ACCESS[activeRoleCode]
  const access =
    isDepartmentManager && (!roleAccess || roleAccess.tree === "v2")
      ? DEPARTMENT_MANAGER_ACCESS
      : roleAccess

  let result = baseForRole
  if (access?.tree === "v2") {
    result = [...baseForRole, ...filterTree([procurementRoute], access)]
  } else if (access?.tree === "pm-append") {
    result = [...baseForRole, ...filterTree(procurementManagerRoutes, access)]
  } else if (access?.tree === "pm-base") {
    result = filterTree(baseForRole, access)
  }

  if (
    isManagerRole(activeRoleCode) &&
    activeRoleCode !== Role.PROCUREMENT_MANAGER &&
    access !== DEPARTMENT_MANAGER_ACCESS
  ) {
    // Fold the approval items into the existing Procurement group (if the role
    // already has one) so the sidenav doesn't show two "Procurement" entries.
    const procurementIdx = result.findIndex((item) => item.key === procurementRoute.key)
    if (procurementIdx === -1) {
      result = [...result, ...managerOrderApprovalRoutes]
    } else {
      const group = result[procurementIdx]
      const mgrItems = managerOrderApprovalRoutes.flatMap((r) => r.collapse || [])
      result = result.map((item, i) =>
        i === procurementIdx
          ? { ...group, collapse: [...(group.collapse || []), ...mgrItems] }
          : item
      )
    }
  }

  return result
}

// Flattens getAccessibleRoutes into a Set of every reachable `route` path
// (walking nested `collapse` arrays at any depth) — cheap membership check
// for "can this role open /procurement/xyz".
export const getAccessibleRoutePaths = (auth, options) => {
  const paths = new Set()
  const walk = (items = []) => {
    items.forEach((item) => {
      if (item.route) paths.add(item.route)
      if (item.collapse) walk(item.collapse)
    })
  }
  walk(getAccessibleRoutes(auth, options))
  return paths
}
